import { SearchInput } from "@/components/admin/search-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Ban, Compass, Grid3x3, Mailbox, Star, Factory } from "lucide-react";
import { FilterLiveForm } from "ra-core";

import { FilterCategory } from "../filters/FilterCategory";
import { Explain } from "./Explain";

/**
 * Facets for the Doors list.
 *
 * Only dimensions that actually discriminate are here. `segment` is
 * distillery_plant for all 5,360 doors — the Plants sheet is the mailing
 * universe — so a segment filter would be a control that does nothing. Same for
 * `confidence`, which reads `confirmed` on every row.
 *
 * `potential` and the permit mix are the two facets carried over from the HTML
 * review page, and they behave very differently. The permit mix splits the list
 * meaningfully — 908 doors hold an Importer or Wholesaler permit alongside
 * their DSP. `potential` barely splits it at all: 26 doors of 5,363 are rated.
 * It is offered anyway, because finding those 26 is exactly the point, and
 * "Unrated" is a filter you would genuinely use to see what still needs
 * judging.
 *
 * Waves are how drops are sequenced geographically. Cells are how they are
 * sequenced in TIME: one cell is one drop, and the 18 of them are balanced at
 * ~283 doors each with a 5% holdout that is never mailed and serves as the
 * response baseline.
 */

/** From the data, not invented: 8 waves across 5,363 doors. The numeric prefix
 *  is part of the stored value, and it is what makes them sort correctly. */
const WAVES = [
  "W1 Bourbon Country",
  "W2 Southeast",
  "W3 Northeast",
  "W4 Midwest",
  "W5 Texas",
  "W6 Mountain",
  "W7 West",
  "W8 Puerto Rico",
];

/** 18 cells plus the holdout, which is never mailed and is the response
 *  baseline the whole experiment depends on. Filtering to HOLDOUT is a
 *  legitimate thing to do -- you may want to SEE them -- which is exactly why
 *  the guard against mailing them lives in plan_drop() and not in this list. */
const CELLS = [
  ...Array.from({ length: 18 }, (_, i) => `C${String(i + 1).padStart(2, "0")}`),
  "HOLDOUT",
];

export const DoorListFilter = () => (
  <div className="w-52 min-w-52 flex flex-col gap-8">
    <FilterLiveForm>
      <SearchInput source="q" />
    </FilterLiveForm>

    <FilterCategory
      icon={
        <span className="flex items-center gap-1">
          <Mailbox className="h-4 w-4" />
          <Explain term="suppressed" />
        </span>
      }
      label="Status"
    >
      {/* `status` is derived in the door_directory view rather than here, so
          the precedence (an opt-out outranks a retirement) is defined once, in
          SQL, and cannot drift between this list and anything else that asks. */}
      <ToggleFilterButton
        className="w-full justify-between"
        label="Mailable"
        value={{ status: "mailable" }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Suppressed"
        value={{ status: "suppressed" }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Retired"
        value={{ status: "retired" }}
      />
    </FilterCategory>

    <FilterCategory
      icon={
        <span className="flex items-center gap-1">
          <Star className="h-4 w-4" />
          <Explain term="potential" />
        </span>
      }
      label="Potential"
    >
      {/* Filtering on `potential` itself, not on potential_rank. The rank
          exists to SORT by meaning; filtering on a number would make the URL
          say `potential_rank=1` where nobody can read it. */}
      <ToggleFilterButton
        className="w-full justify-between"
        label="High"
        value={{ potential: "high" }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Low"
        value={{ potential: "low" }}
      />
      {/* The useful one: 5,337 doors nobody has judged yet. */}
      <ToggleFilterButton
        className="w-full justify-between"
        label="Unrated"
        value={{ "potential@is": null }}
      />
    </FilterCategory>

    <FilterCategory
      icon={
        <span className="flex items-center gap-1">
          <Factory className="h-4 w-4" />
          <Explain term="permits" />
        </span>
      }
      label="Permits"
    >
      <ToggleFilterButton
        className="w-full justify-between"
        label="Also imports / wholesales"
        value={{ beyond_dsp: true }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Distilling only"
        value={{ beyond_dsp: false }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Several permits"
        value={{ "permit_count@gt": 1 }}
      />
    </FilterCategory>

    <FilterCategory
      icon={
        <span className="flex items-center gap-1">
          <Compass className="h-4 w-4" />
          <Explain term="wave" />
        </span>
      }
      label="Wave"
    >
      {WAVES.map((w) => (
        <ToggleFilterButton
          className="w-full justify-between"
          key={w}
          label={w}
          value={{ wave: w }}
        />
      ))}
    </FilterCategory>

    <FilterCategory
      icon={
        <span className="flex items-center gap-1">
          <Grid3x3 className="h-4 w-4" />
          <Explain term="cell" />
        </span>
      }
      label="Cell"
    >
      {/* One cell is one drop. Assignment is a pure function of
          (ZIP, House #) and the pinned cell_buckets.json, so it survives TTB
          re-typing a street — which is the whole reason "already mailed touch
          3" stays true across rebuilds. */}
      {CELLS.map((c) => (
        <ToggleFilterButton
          className="w-full justify-between"
          key={c}
          label={c}
          value={{ cell: c }}
        />
      ))}
    </FilterCategory>

    <FilterCategory icon={<Ban className="h-4 w-4" />} label="Activity">
      <ToggleFilterButton
        className="w-full justify-between"
        label="Already mailed"
        value={{ "touches_sent@gt": 0 }}
      />
      <ToggleFilterButton
        className="w-full justify-between"
        label="Promoted"
        value={{ "company_id@not.is": null }}
      />
      {/*
        THERE IS DELIBERATELY NO "has open review" FILTER.
        door_directory.open_reviews counts review_queue rows whose address_ref
        points at this door -- and for the two open types that is almost always
        zero, for structural reasons rather than a wiring fault:

          merge  address_ref is set only where (zip, house_number) matches
                 EXACTLY ONE mailable door. A merge candidate is by definition a
                 key that matches two or more, so it can never link. The
                 ambiguity is the reason the question exists.
          supp   these are addresses the pipeline already excluded from Plants,
                 so they are usually not in `addresses` at all. 1 of 149 links.

        A filter that matches one door out of 5,363 is a control that does
        nothing, which is worse than no control. Linking doors to review items
        properly needs a many-to-many table -- one review item, several
        candidate doors. Worth doing if the Doors list ever needs to surface
        triage; not worth faking here. Use the Review screen instead.
      */}
    </FilterCategory>
  </div>
);
